import { resumeFromApplication } from './shared';

export const LA_IDS = {
    isReturning: 'la-is-returning-yes',
    canAttendOrientation: 'la-can-attend-orientation-yes',
    canAttendPedagogy: 'la-can-attend-pedagogy-yes',
    interestStatement: 'la-interest-statement',
    priorExperience: 'la-prior-experience',
    priorRoles: 'la-prior-role-ta',
    otherRoleInterest: 'la-other-role-tutor',
    commitments: 'la-commitments-acknowledged',
};

export const LA_QUESTIONS = {
    isReturning:
        'Have you worked as a Learning Assistant at MSU Denver before?',
    canAttendOrientation:
        'The LA role begins with a required, paid 2-day Orientation (10-12 hours) the week before the term starts. Are you able to attend?',
    canAttendPedagogy:
        "New Learning Assistants also attend the LA program's pedagogy seminar during the term. Are you able to attend?",
    interestStatement:
        'Why do you want to be a Learning Assistant? (A few sentences is plenty.)',
    priorExperience:
        'What qualities or experiences would make you an effective LA?',
    priorRoles:
        'Have you held any of these paid peer-support roles before? (Select all that apply.)',
    otherRoleInterest:
        'Besides being an LA, would you also be interested in any of these roles? (Select all that apply.)',
};

// The roles an LA applicant may also be interested in. `program` is the
// ProgramConfig whose page the link points to; Tutor has no program, so its
// link is the platform's tutoring page. "No, just LA" stands alone.
export const OTHER_ROLES = [
    { value: 'tutor', label: 'Tutor', program: null },
    { value: 'si', label: 'SI Leader', program: 'si' },
    { value: 'ta', label: 'TA', program: 'ta' },
    { value: 'none', label: 'No, just LA', program: null },
];

export const otherRoleInterestText = (roles) =>
    roles?.length
        ? roles
              .map(
                  (value) =>
                      OTHER_ROLES.find((role) => role.value === value)?.label ||
                      value
              )
              .join(', ')
        : 'Not answered';

export const PRIOR_ROLES = [
    { value: 'ta', label: 'Paid Teaching Assistant (TA)' },
    { value: 'si', label: 'Paid Supplemental Instruction (SI) leader' },
    { value: 'la', label: 'Paid Learning Assistant (LA)' },
    { value: 'tutor', label: 'Paid tutor' },
    { value: 'none', label: 'None of these' },
];

// The same text the server refuses with (studentApplicationPrograms.js); a
// new LA answering No to either attendance question cannot apply as an LA.
export const LA_ATTENDANCE_REFUSAL =
    "The LA program requires new Learning Assistants to attend both the orientation and the pedagogy seminar, so you can't apply as an LA this term. If you have questions, contact the LA program.";

// The same refusal for a nominated student, who accepts rather than applies.
export const LA_ATTENDANCE_REFUSAL_NOMINEE = LA_ATTENDANCE_REFUSAL.replace(
    "can't apply as an LA",
    "can't accept as an LA"
);

const ATTENDANCE_LABEL = {
    orientation:
        "Orientation: new Learning Assistants must attend, so you can't apply as an LA this term",
    pedagogy:
        "Pedagogy seminar: new Learning Assistants must attend, so you can't apply as an LA this term",
};

export const MAX_ESSAY_LENGTH = 3000;

export const LA_ERROR_MESSAGES = {
    [LA_IDS.isReturning]:
        'Answer whether you have worked as a Learning Assistant at MSU Denver before.',
    [LA_IDS.canAttendOrientation]:
        'Answer whether you are able to attend the Orientation.',
    [LA_IDS.canAttendPedagogy]:
        "Answer whether you are able to attend the LA program's pedagogy seminar.",
    [LA_IDS.interestStatement]:
        'Tell us why you want to be a Learning Assistant.',
    [LA_IDS.priorExperience]: 'Tell us what would make you an effective LA.',
    [LA_IDS.priorRoles]: 'Choose at least one option.',
    [LA_IDS.commitments]: 'Acknowledge the Learning Assistant commitments.',
};

const NONE_WITH_OTHERS_MESSAGE =
    '"None of these" cannot be combined with another choice.';

export const emptyAnswers = Object.freeze({
    isReturning: null,
    canAttendOrientation: null,
    canAttendPedagogy: null,
    interestStatement: '',
    priorExperience: '',
    priorRoles: Object.freeze([]),
    otherRoleInterest: Object.freeze([]),
    commitmentsAcknowledged: false,
    resume: null,
});

const boolOrNull = (value) => (typeof value === 'boolean' ? value : null);

export function fromApplication(application) {
    const details = application?.laDetails || {};
    return {
        isReturning: boolOrNull(details.isReturning),
        canAttendOrientation: boolOrNull(details.canAttendOrientation),
        canAttendPedagogy: boolOrNull(details.canAttendPedagogy),
        interestStatement: application?.interestStatement || '',
        priorExperience: application?.priorExperience || '',
        priorRoles: [...(details.priorRoles || [])],
        otherRoleInterest: [...(details.otherRoleInterest || [])],
        commitmentsAcknowledged:
            application?.commitments?.acknowledged === true,
        resume: resumeFromApplication(application),
    };
}

// Attendance answers belong to new Learning Assistants only; the server
// stores a returning LA's as unset, so they are not sent.
export function toPayload(answers) {
    const laDetails = { priorRoles: [...answers.priorRoles] };
    // Informational and optional: an empty list is the question unanswered.
    if (answers.otherRoleInterest.length)
        laDetails.otherRoleInterest = [...answers.otherRoleInterest];
    if (answers.isReturning !== null)
        laDetails.isReturning = answers.isReturning;
    if (answers.isReturning === false) {
        if (answers.canAttendOrientation !== null)
            laDetails.canAttendOrientation = answers.canAttendOrientation;
        if (answers.canAttendPedagogy !== null)
            laDetails.canAttendPedagogy = answers.canAttendPedagogy;
    }
    return {
        laDetails,
        interestStatement: answers.interestStatement,
        priorExperience: answers.priorExperience,
        commitments: { acknowledged: answers.commitmentsAcknowledged === true },
    };
}

// The new-or-returning question and, for a new LA, the two attendance
// questions. They sit in the Eligibility step so a No stops the student before
// they rank positions.
export function getEligibilityErrors(answers) {
    const open = [];
    const add = (id, label = LA_ERROR_MESSAGES[id]) => open.push({ id, label });

    if (answers.isReturning === null) add(LA_IDS.isReturning);
    if (answers.isReturning === false) {
        if (answers.canAttendOrientation === null)
            add(LA_IDS.canAttendOrientation);
        if (answers.canAttendPedagogy === null) add(LA_IDS.canAttendPedagogy);
        if (answers.canAttendOrientation === false)
            add(LA_IDS.canAttendOrientation, ATTENDANCE_LABEL.orientation);
        else if (answers.canAttendPedagogy === false)
            add(LA_IDS.canAttendPedagogy, ATTENDANCE_LABEL.pedagogy);
    }
    return open;
}

// The questions asked in the Learning Assistant Questions step. The resume has
// its own block (blocks/Resume.js) and never appears here. The LA program's
// commitments can't be saved empty, so the acknowledgment is always required.
export function getQuestionErrors(answers) {
    const open = [];
    const add = (id, label = LA_ERROR_MESSAGES[id]) => open.push({ id, label });

    if (!answers.interestStatement.trim()) add(LA_IDS.interestStatement);
    if (!answers.priorExperience.trim()) add(LA_IDS.priorExperience);
    if (!answers.priorRoles.length) add(LA_IDS.priorRoles);
    else if (
        answers.priorRoles.includes('none') &&
        answers.priorRoles.length > 1
    )
        add(LA_IDS.priorRoles, NONE_WITH_OTHERS_MESSAGE);
    if (answers.commitmentsAcknowledged !== true) add(LA_IDS.commitments);
    return open;
}

// Every open question in the order the wizard asks them.
export function getErrors(answers) {
    return [...getEligibilityErrors(answers), ...getQuestionErrors(answers)];
}
